import React, { useEffect, useState } from "react";
import { Alert, Button, Card } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import paths from "routes/paths";
import olreadiaService from "services/olreadiaService";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import BackgroundChecksPanel from "./BackgroundChecksPanel";

export default function BackgroundChecksPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const seededProfile = location.state?.profile || null;
  const seededHelperProfile = location.state?.helperProfile || null;
  const seededProfileId = seededProfile?.id || searchParams.get("profileId") || "";

  const [loading, setLoading] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(seededProfile);
  const [error, setError] = useState(null);
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);

  const reloadProfile = async () => {
    if (!seededProfileId) return;
    setLoading(true);
    setError(null);
    try {
      const profile = await olreadiaService.professionProfiles.get(seededProfileId);
      setSelectedProfile(profile || null);
    } catch (err) {
      setSelectedProfile(null);
      setError(err?.response?.data?.message || err?.message || "Failed to load profession profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const loadProfile = async () => {
      if (seededProfile) {
        setSelectedProfile(seededProfile);
        return;
      }

      if (!seededProfileId) {
        setSelectedProfile(null);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const profile = await olreadiaService.professionProfiles.get(seededProfileId);
        if (isMounted) {
          setSelectedProfile(profile || null);
        }
      } catch (err) {
        if (isMounted) {
          setSelectedProfile(null);
          setError(err?.response?.data?.message || err?.message || "Failed to load profession profile");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadProfile();
    return () => {
      isMounted = false;
    };
  }, [seededProfile, seededProfileId]);

  return (
    <div className="container-fluid mt-4" style={{ maxWidth: 980, margin: "0 auto" }}>
      <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-2">
        <div style={{ minWidth: 0, flex: 1 }}>
          <WidgetSectionTitle
            icon="briefcase"
            transform="shrink-2"
            title="Background Checks"
            subtitle={selectedProfile
              ? "Manage and review background checks for this profession profile."
              : "Search and manage background checks across profession profiles."}
            className="mb-1"
          />
         
         
        </div>
        <Button 
        type="primary"
        icon={<ArrowLeftOutlined />} 
        onClick={() => navigate(paths.helperProfessionProfiles)}>
          Profession Profiles
        </Button>
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load profession profile"
          description={error}
          style={{ marginBottom: 12 }}
        />
      ) : null}

      {loading && !selectedProfile ? (
        <Card style={{ borderRadius: 14, border: "none", boxShadow: "none" }}>
          <Alert type="info" showIcon message="Loading profile checks" description="Preparing the background-check workspace." />
        </Card>
      ) : (
        <BackgroundChecksPanel
          profile={selectedProfile}
          helperProfile={seededHelperProfile}
          professionProfileId={selectedProfile?.id}
          userId={selectedProfile?.userId || selectedProfile?.helperId}
          profileName={selectedProfile?.profileName || selectedProfile?.userFullName || "Profession Profile"}
          sectorLabel={selectedProfile?.sector?.name || selectedProfile?.sector?.code || selectedProfile?.sectorCode || "-"}
          createDrawerOpen={createDrawerOpen}
          onCreateDrawerOpenChange={setCreateDrawerOpen}
          onReloadProfile={reloadProfile}
        />
      )}
    </div>
  );
}
